import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Dialog, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import AntSwitch from '@/components/form-control/AntSwitch'
import { useSelector } from 'react-redux'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import toast from 'react-hot-toast'
import flowSensorApi from '@/api/config/flowSensorApi'
import { FlowSensorType, InputOptions } from '@/types/hydraulic'
import { useUnits } from '@/hooks/useUnits'
import useTranslation from '@/hooks/useTranslation'
import DataGrid, { gridCellClasses } from '@/components/grid/DataGrid'
import type { GridCellCtx, GridColumn } from '@/components/grid/types'
import DeleteIconButton from '@/features/config/components/common/DeleteIconButton'
import EmptyList from './EmptyList'
import CardsSkeleton from './CardsSkeleton'
import SensorCards from './SensorCards'
import { useRuleCopy } from './use-rule-copy'
import { writeSerially } from './serialWrite'
import useDraftRows, { isDraftRowId } from './use-draft-rows'
import useWaterSourceUsage from '@/features/config/hooks/watersource/useWaterSourceUsage'
import { getApiErrorMessage } from '@/helpers/notify'
import ConfirmDeleteModalUI from '@/components/common/ConfirmDeleteModalUI/ConfirmDeleteModalUI'
import DeleteDeviceDetails from './DeleteDeviceDetails'

const ROW_HEIGHT = 42
const HEADER_HEIGHT = 40
const MAX_VISIBLE_ROWS = 6

interface SensorsGridProps {
  interfaceId: number
  flowRuleId: number
  sensors: FlowSensorType[]
  /** The interface's input + SDI-12 decoders — what a sensor can be wired to. */
  options: InputOptions[]
  /**
   * Every flow sensor on the interface, across all rules. A decoder one of them
   * already uses is not offered again.
   */
  takenSensors?: FlowSensorType[]
  /** Only a control hub has more than one controller to name. */
  showController?: boolean
  /** The controller's own timezone, for reading a sensor's last-recorded stamp. */
  timeZone?: string | null
  /** First load only — placeholder cards instead of the empty state. */
  loading?: boolean
  isViewOnly?: boolean
  onChanged: () => Promise<unknown> | void
  /**
   * SCRUM-1032 QC item 6 — whether there is staged work the popover would throw
   * away, so it can ask before closing rather than losing it silently.
   */
  onDirtyChange?: (dirty: boolean) => void
}

interface SensorRow {
  id: number
  active: boolean
  io: string
  /** The decoder's address-index, shown under its name on the cards. */
  decoderRef: string
  controller?: string
  measuredFlow: number
  usage: number
  /** Controller timestamp of the newest hourly volume bucket. */
  volumeLastTs: number
  isCreateRow?: boolean
}

/** Which decoder a sensor sits on — the identity the two lists are joined by. */
const decoderKey = (part: { address: unknown; index: unknown }) =>
  `${Number(part.address)}-${Number(part.index)}`

/**
 * The picker's option value.
 *
 * It carries all three lines' text because Autocomplete filters on this string,
 * so typing a decoder name, its address or its controller all find the row. It
 * is also the key the save maps back to an option, which is why it has to be
 * unique per decoder.
 */
const optionLabel = (option: InputOptions) =>
  [option.name, decoderKey(option), option.twiCustomName]
    .filter(Boolean)
    .join(' · ')

/**
 * The rule's sensors, with the same bulk-edit language as the other config
 * lists: fill and border on editable cells, one Save for the whole list, a blank
 * create row at the bottom instead of an Add button.
 */
const SensorsGrid = ({
  interfaceId,
  flowRuleId,
  sensors,
  options,
  takenSensors,
  showController,
  timeZone,
  loading,
  isViewOnly,
  onChanged,
  onDirtyChange,
}: SensorsGridProps) => {
  const copy = useRuleCopy()
  const { t } = useTranslation()
  const { convertLabelUnit, convertFromMetricV2 } = useUnits()
  const isDark = useSelector(selectTheme) === mode_type.dark
  const [bulkEditMode, setBulkEditMode] = useState(false)
  const [saving, setSaving] = useState(false)
  // Pending edits are held here rather than staged in the grid: the panel
  // refetches its sensors whenever anything changes, which would replace the
  // rows underneath unsaved values.
  const [edits, setEdits] = useState<Record<string, Partial<SensorRow>>>({})
  const { draftIds, addDraft, removeDraft, resetDrafts } = useDraftRows()
  const {
    sensorUsedBy,
    ready: usageReady,
    error: usageError,
  } = useWaterSourceUsage(interfaceId)
  // Until the water sources are read, the map says nothing uses anything:
  // the delete waits for it (SCRUM-1032 review).
  const usageLoading = !usageReady && !usageError
  // The row the user asked to delete, held until they confirm it.
  const [pendingDelete, setPendingDelete] = useState<SensorRow | null>(null)
  const [deleting, setDeleting] = useState(false)

  const optionByLabel = useMemo(() => {
    const map = new Map<string, InputOptions>()
    options.forEach((option) => map.set(optionLabel(option), option))
    return map
  }, [options])

  /** The decoder each existing sensor sits on, so a row can keep its own. */
  const decoderKeyBySensorId = useMemo(() => {
    const map = new Map<string, string>()
    ;(takenSensors ?? []).forEach((sensor) =>
      map.set(String(sensor.id), decoderKey(sensor)),
    )
    return map
  }, [takenSensors])

  const takenDecoderKeys = useMemo(
    () => new Set((takenSensors ?? []).map(decoderKey)),
    [takenSensors],
  )

  /**
   * The decoders this row may pick: everything not already wired to a flow
   * sensor, plus whatever this row is on now — a row editing its own IO must
   * still see the value it currently holds.
   */
  const optionsForRow = useCallback(
    (rowId: number) => {
      const own = decoderKeyBySensorId.get(String(rowId))
      return options
        .filter((option) => {
          const key = decoderKey(option)
          return key === own || !takenDecoderKeys.has(key)
        })
        .map(optionLabel)
    },
    [options, takenDecoderKeys, decoderKeyBySensorId],
  )

  /**
   * Three lines per option, matching the station form's decoder picker: the
   * decoder's name, its address-index, then the controller — which only a
   * control hub fills in, so on a plain interface that line is absent.
   */
  const renderOption = useCallback(
    (label: string) => {
      const option = optionByLabel.get(label)
      if (!option) return label
      return (
        <Stack sx={{ gap: '2px', py: '2px' }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {option.name || decoderKey(option)}
          </Box>
          {option.name ? (
            <Box component="span" sx={{ opacity: 0.75 }}>
              {decoderKey(option)}
            </Box>
          ) : null}
          {showController && option.twiCustomName ? (
            <Box component="span" sx={{ opacity: 0.75 }}>
              {option.twiCustomName}
            </Box>
          ) : null}
        </Stack>
      )
    },
    [optionByLabel, showController],
  )

  const rows = useMemo<SensorRow[]>(() => {
    const mapped: SensorRow[] = sensors.map((sensor) => ({
      id: sensor.id,
      active: Boolean(sensor.active),
      io:
        sensor.io ||
        `${sensor.address}-${sensor.index}${
          sensor.twiCustomName ? ` · ${sensor.twiCustomName}` : ''
        }`,
      decoderRef: decoderKey(sensor),
      controller: sensor.twiCustomName ?? '',
      measuredFlow: Number(sensor.measuredFlow ?? 0),
      volumeLastTs: Number(sensor.volumeLastTs ?? 0),
      usage: (sensor.volume ?? []).reduce((sum, value) => sum + Number(value || 0), 0),
    }))
    if (bulkEditMode && !isViewOnly) {
      draftIds.forEach((id) =>
        mapped.push({
          id,
          active: true,
          io: '',
          decoderRef: '',
          controller: '',
          measuredFlow: 0,
          volumeLastTs: 0,
          usage: 0,
          isCreateRow: true,
        }),
      )
    }
    return mapped.map((row) => {
      const edit = edits[String(row.id)]
      return edit ? { ...row, ...edit } : row
    })
  }, [sensors, bulkEditMode, isViewOnly, edits, draftIds])

  const editable = bulkEditMode && !isViewOnly

  // SCRUM-1032 QC item 6 — tell the popover when closing would lose something.
  // A draft row counts even before anything is typed into it.
  const hasStagedEdits = Object.keys(edits).length > 0 || draftIds.length > 0
  useEffect(() => {
    onDirtyChange?.(hasStagedEdits)
    return () => onDirtyChange?.(false)
  }, [hasStagedEdits, onDirtyChange])

  const getRowId = useCallback((row: SensorRow) => String(row.id), [])

  // Editable cells carry the shared fill; a cell the user has touched carries
  // the pending-change colour, and read-only columns dim instead of filling.
  const cellClassFor = useCallback(
    (field: keyof SensorRow, isFieldEditable: boolean) =>
      ({ row }: GridCellCtx<SensorRow>) => {
        if (!bulkEditMode || !isFieldEditable) return ''
        return edits[String(row.id)]?.[field] !== undefined
          ? gridCellClasses.changed
          : gridCellClasses.editable
      },
    [bulkEditMode, edits],
  )

  /**
   * The switch writes straight into the pending edits — there is no cell
   * editor to commit, and the merged `rows` above already carry the new value.
   */
  const toggleActive = useCallback((rowId: number, next: boolean) => {
    const key = String(rowId)
    setEdits((prev) => ({ ...prev, [key]: { ...(prev[key] ?? {}), active: next } }))
  }, [])

  const handleDelete = useCallback(
    async (id: number) => {
      if (isViewOnly) return
      if (!usageReady) {
        toast.error(getApiErrorMessage(usageError, t('somethingWentWrong')))
        return
      }
      // A water source points at the flow sensor it meters through, so a
      // sensor one of them uses never reaches the delete endpoint.
      const usedBy = sensorUsedBy.get(Number(id))
      if (usedBy?.length) {
        toast.error(
          t('flowSensorUsedByWaterSource', { names: usedBy.join(', ') }),
        )
        return
      }
      try {
        await flowSensorApi.delete(String(interfaceId), String(id))
        await onChanged()
      } catch (error: any) {
        toast.error(getApiErrorMessage(error, t('commonDeleteFailed')))
      }
    },
    [t, interfaceId, isViewOnly, onChanged, sensorUsedBy, usageReady, usageError],
  )

  const columns = useMemo<GridColumn<SensorRow>[]>(() => {
    const base: GridColumn<SensorRow>[] = [
      {
        field: 'active',
        headerName: t('commonLabelsStatus'),
        width: 140,
        // A switch is the control, so the cell never opens an editor.
        editable: false,
        cellClass: cellClassFor('active', true),
        cellRenderer: ({ row }) => (
          <Stack
            direction="row"
            data-no-cell-edit
            sx={{ alignItems: 'center', gap: '8px' }}
          >
            <AntSwitch
              width={34}
              height={18}
              widthThumb={14}
              heightThumb={14}
              borderThumb={14}
              borderTrack={9}
              transform="16"
              checked={Boolean(row.active)}
              disabled={!editable}
              // Read-only draws exactly like editable. AntSwitch's disabled
              // state paints a white thumb on a `background.default` track
              // (white on white in light mode), and dimming it instead just
              // made an in-use sensor look switched off — the state is what
              // this cell is FOR. "You cannot edit this" is already said by the
              // panel showing Edit rather than Cancel/Save, and by every other
              // cell here staying plain text until edit mode.
              sx={{
                '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                  opacity: 1,
                  backgroundColor: '#cddada',
                },
                '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track':
                  {
                    opacity: 1,
                    backgroundColor: isDark ? '#5bcf86' : '#004744',
                  },
                '& .MuiSwitch-switchBase.Mui-disabled .MuiSwitch-thumb': {
                  opacity: 1,
                  backgroundColor: '#fff',
                },
              }}
              onChange={(event) => toggleActive(row.id, event.target.checked)}
            />
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: row.active ? 'text.main' : 'grey.600',
              }}
            >
              {row.active ? copy.sensorInUse : copy.sensorNotInUse}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'io',
        headerName: copy.io,
        flex: 1,
        minWidth: 200,
        editable,
        editor: 'select',
        // Resolved per row: each one may keep the decoder it already sits on.
        editorParams: ({ row }: GridCellCtx<SensorRow>) => ({
          values: optionsForRow(row.id),
          renderOption,
          // Three lines per option need more room than the IO column has.
          minWidth: 320,
        }),
        // SCRUM-1032 QC item 5 — the chevron that says this cell picks from a
        // list, shown whenever the cell can actually be edited.
        cellClass: (ctx: GridCellCtx<SensorRow>) => [
          cellClassFor('io', true)(ctx),
          editable ? gridCellClasses.dropdown : '',
        ],
        cellRenderer: ({ row, value }) =>
          value ? (
            <>{value as string}</>
          ) : (
            <Box component="span" sx={{ color: 'grey.600', fontStyle: 'italic' }}>
              {row.isCreateRow ? copy.pickInput : ''}
            </Box>
          ),
      },
      // No Flow or Usage columns: both are readings from the controller, not
      // settings, and this table only exists in bulk edit. The cards show them.
    ]

    if (!editable) return base

    return [
      ...base,
      {
        field: '__actions',
        headerName: '',
        width: 90,
        pinned: 'right',
        sortable: false,
        resizable: false,
        cellRenderer: ({ row }) => (
          <Box data-no-cell-edit>
            <DeleteIconButton
              isDark={isDark}
              title={row.isCreateRow ? 'Discard' : t('actionDelete')}
              onClick={() => {
                // A draft has never been written, so it is dropped locally
                // rather than going anywhere near the delete endpoint.
                if (row.isCreateRow) {
                  removeDraft(row.id)
                  setEdits((prev) => {
                    const next = { ...prev }
                    delete next[String(row.id)]
                    return next
                  })
                  return
                }
                // A sensor a water source meters through can never be
                // deleted, so it is refused here rather than after a
                // confirmation the user cannot act on.
                const usedBy = sensorUsedBy.get(Number(row.id))
                if (usedBy?.length) {
                  toast.error(
                    t('flowSensorUsedByWaterSource', {
                      names: usedBy.join(', '),
                    }),
                  )
                  return
                }
                setPendingDelete(row)
              }}
            />
          </Box>
        ),
      },
    ]
  }, [
    copy,
    editable,
    isDark,
    toggleActive,
    optionsForRow,
    renderOption,
    t,
    cellClassFor,
    sensorUsedBy,
    removeDraft,
  ])

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return
    setDeleting(true)
    try {
      await handleDelete(pendingDelete.id)
    } finally {
      setDeleting(false)
      setPendingDelete(null)
    }
  }, [handleDelete, pendingDelete])

  const handleCellValueChanged = useCallback(
    (event: {
      row: SensorRow
      field: string
      newValue: unknown
    }) => {
      const key = String(event.row.id)
      const patch =
        event.field === 'active'
          ? { active: event.newValue === 'Active' }
          : { [event.field]: event.newValue }

      setEdits((prev) => ({ ...prev, [key]: { ...(prev[key] ?? {}), ...patch } }))
      // The merged `rows` above already carry the new value, so letting the grid
      // stage it as well would double-count it.
      return false as const
    },
    [],
  )

  const handleSave = useCallback(async () => {
    const entries = Object.entries(edits)
    if (entries.length === 0) {
      setBulkEditMode(false)
      return
    }
    setSaving(true)

    const payloads = entries
      .map(([id, edit]) => {
        const row = rows.find((entry) => String(entry.id) === id)
        const option = optionByLabel.get(String(edit.io ?? row?.io ?? ''))
        const existing = sensors.find((sensor) => String(sensor.id) === id)
        // A create row needs an IO before it means anything.
        if (isDraftRowId(Number(id)) && !option) return null
        return {
          id: isDraftRowId(Number(id)) ? undefined : Number(id),
          interfaceId,
          flowRuleId,
          active: edit.active ?? row?.active ?? existing?.active ?? true,
          decoderId: option ? option.id || undefined : (existing as any)?.decoderId || undefined,
          address: option ? option.address : Number(existing?.address ?? 0),
          index: option ? option.index : Number(existing?.index ?? 0),
          twiId: option?.twiId ?? (existing as any)?.twiId ?? null,
        }
      })
      .filter(Boolean) as any[]

    const { failed, lastError } = await writeSerially(payloads, (payload) =>
      payload.id ? flowSensorApi.update(payload) : flowSensorApi.create(payload),
    )

    setSaving(false)
    if (failed.length > 0) {
      toast.error(
        getApiErrorMessage(
          lastError,
          t('bulkSavePartialError', {
            failed: failed.length,
            total: payloads.length,
          }),
        ),
      )
      return
    }
    setEdits({})
    resetDrafts()
    setBulkEditMode(false)
    await onChanged()
  }, [
    t,
    edits,
    flowRuleId,
    interfaceId,
    onChanged,
    optionByLabel,
    rows,
    sensors,
    resetDrafts,
  ])

  const handleCancel = useCallback(() => {
    setEdits({})
    resetDrafts()
    setBulkEditMode(false)
  }, [resetDrafts])

  // Above this the list scrolls internally instead of growing the row.
  const tall = rows.length > MAX_VISIBLE_ROWS

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'border.main',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: '8px',
          px: '12px',
          py: '9px',
          borderBottom: '1px solid',
          borderColor: 'border.main',
        }}
      >
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'grey.800',
          }}
        >
          {copy.sensors}
          <Box component="span" sx={{ ml: '6px', fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: 'grey.600' }}>
            {sensors.length}
          </Box>
        </Typography>
        <Box sx={{ flex: 1 }} />
        {!isViewOnly &&
          (bulkEditMode ? (
            <>
              <Button variant="secondary" size="small" onClick={handleCancel}>
                {t('commonActionsCancel')}
              </Button>
              <Button variant="primary" size="small" disabled={saving} onClick={handleSave}>
                {t('commonActionsSave')}
              </Button>
            </>
          ) : (
            <Button variant="secondary" size="small" onClick={() => setBulkEditMode(true)}>
              {t('commonActionsBulkEdit')}
            </Button>
          ))}
      </Stack>

      {loading && rows.length === 0 ? (
        <CardsSkeleton isDark={isDark} />
      ) : rows.length === 0 ? (
        <EmptyList
          glyph="sensor"
          title={copy.emptySensorTitle}
          hint={copy.emptySensorHint}
          actionLabel={copy.emptySensorAction}
          disabled={isViewOnly}
          onAction={() => {
            setBulkEditMode(true)
            addDraft()
          }}
        />
      ) : !editable ? (
        // Reading and editing want different shapes, as with the valves.
        <SensorCards
          rows={rows}
          isDark={isDark}
          showController={showController}
          timeZone={timeZone}
          formatFlow={(value) =>
            String(convertFromMetricV2(Number(value ?? 0), 'l/m', 'gpm', 2))
          }
          flowUnit={convertLabelUnit('l/m')}
        />
      ) : (
        // Short lists size to their content so the panel is just a tall row in
        // the pipe list — one list, one scrollbar. A long list caps out and
        // scrolls inside itself, which is the only point where a second scroll
        // area is worth having.
        <Box
          sx={{
            width: '100%',
            ...(tall
              ? { height: `${MAX_VISIBLE_ROWS * ROW_HEIGHT + HEADER_HEIGHT}px` }
              : {}),
            // The card border belongs to the panel above, not to the grid.
            '& > [role="grid"]': {
              border: 'none',
              borderRadius: 0,
              boxShadow: 'none',
            },
          }}
        >
          <DataGrid<SensorRow>
            columns={columns}
            rows={rows}
            getRowId={getRowId}
            autoHeight={!tall}
            rowHeight={ROW_HEIGHT}
            headerHeight={HEADER_HEIGHT}
            editMode={editable ? 'singleClick' : 'none'}
            onCellValueChanged={handleCellValueChanged}
          />
        </Box>
      )}

      {/* Sits under the list, where the row it adds will appear — rather than
          up in the header beside Cancel and Save, which are about the batch as
          a whole. */}
      {editable && rows.length > 0 && (
        <Stack
          direction="row"
          sx={{
            borderTop: '1px solid',
            borderColor: 'border.main',
            px: '8px',
            py: '6px',
          }}
        >
          <Button
            variant="secondary"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={() => addDraft()}
          >
            Add sensor
          </Button>
        </Stack>
      )}

      <Dialog
        open={Boolean(pendingDelete)}
        onClose={() => !deleting && setPendingDelete(null)}
      >
        <ConfirmDeleteModalUI
          title={t('DeleteFlowSensor')}
          name={pendingDelete?.io}
          text={t('flowSensorDeleteConfirmation')}
          isLoading={deleting || usageLoading}
          onClose={() => setPendingDelete(null)}
          onConfirm={handleConfirmDelete}
        >
          <DeleteDeviceDetails
            details={[
              { label: copy.io, value: pendingDelete?.decoderRef },
              {
                label: t('formsLabelsController'),
                value: showController ? pendingDelete?.controller : '',
              },
              {
                label: t('commonLabelsStatus'),
                value: pendingDelete?.active
                  ? copy.active
                  : t('commonStatusDisabled'),
              },
            ]}
          />
        </ConfirmDeleteModalUI>
      </Dialog>
    </Box>
  )
}

export default SensorsGrid
